import { getCurrentSession, isSupabaseConfigured } from "@/lib/supabase";
import type {
  ClickUpDoc,
  ClickUpDocPage,
  ClickUpListOption,
  ClickUpMember,
  ClickUpTask,
  ClickUpTeam,
  CreateClickUpTaskRequest,
  CreateClickUpTasksBatchResult,
} from "@/types/clickup";

const SUPABASE_PROJECT_URL = "https://tsnbktwryvnbmgsvzxdt.supabase.co";

function getBaseUrl(): string {
  if (import.meta.env.DEV) {
    return "/api/clickup";
  }
  return `${SUPABASE_PROJECT_URL}/functions/v1/clickup-proxy`;
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const session = await getCurrentSession();
  if (!session?.access_token) {
    throw new Error("Please log in to access ClickUp");
  }
  return {
    Authorization: `Bearer ${session.access_token}`,
    "Content-Type": "application/json",
  };
}

async function parseError(response: Response): Promise<never> {
  let message = response.statusText || "Request failed";
  try {
    const body = await response.json();
    if (body?.error && typeof body.error === "string") {
      message = body.error;
    }
  } catch {
    // ignore
  }
  throw new Error(message);
}

class ClickUpService {
  private async taskRequest(path:string,method="GET",body?:unknown):Promise<Record<string,unknown>> {
    if(!isSupabaseConfigured)throw new Error("Service not configured");
    const response=await fetch(getBaseUrl()+path,{method,headers:await getAuthHeaders(),...(body===undefined?{}:{body:JSON.stringify(body)})});
    if(!response.ok)await parseError(response);
    return response.json();
  }
  async getTask(taskId:string):Promise<ClickUpTask>{const data=await this.taskRequest("/tasks/"+encodeURIComponent(taskId));return data.task as ClickUpTask;}
  async listFields(listId:string):Promise<Array<Record<string,unknown>>>{const data=await this.taskRequest("/lists/"+encodeURIComponent(listId)+"/fields");return (data.fields??[]) as Array<Record<string,unknown>>;}
  async updateTask(taskId:string,fields:{name?:string;description?:string;status?:string;parent?:string}):Promise<ClickUpTask>{const data=await this.taskRequest("/tasks/"+encodeURIComponent(taskId),"PUT",fields);return data.task as ClickUpTask;}
  async setTaskField(taskId:string,fieldId:string,value:unknown){return this.taskRequest("/tasks/"+encodeURIComponent(taskId)+"/fields/"+encodeURIComponent(fieldId),"POST",{value});}
  async commentTask(taskId:string,text:string){return this.taskRequest("/tasks/"+encodeURIComponent(taskId)+"/comments","POST",{comment_text:text});}
  async listTeams(): Promise<ClickUpTeam[]> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured");
    }

    const response = await fetch(`${getBaseUrl()}/teams`, {
      method: "GET",
      headers: await getAuthHeaders(),
    });

    if (!response.ok) {
      await parseError(response);
    }

    const data = await response.json();
    return data.teams ?? [];
  }

  async listMembers(teamId: string): Promise<ClickUpMember[]> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured");
    }

    const id = teamId.trim();
    if (!id) throw new Error("Team ID is required");

    const response = await fetch(
      `${getBaseUrl()}/teams/${encodeURIComponent(id)}/members`,
      {
        method: "GET",
        headers: await getAuthHeaders(),
      }
    );

    if (!response.ok) {
      await parseError(response);
    }

    const data = await response.json();
    return data.members ?? [];
  }

  async listLists(teamId: string): Promise<ClickUpListOption[]> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured");
    }

    const id = teamId.trim();
    if (!id) throw new Error("Team ID is required");

    const response = await fetch(
      `${getBaseUrl()}/teams/${encodeURIComponent(id)}/lists`,
      {
        method: "GET",
        headers: await getAuthHeaders(),
      }
    );

    if (!response.ok) {
      await parseError(response);
    }

    const data = await response.json();
    return data.lists ?? [];
  }

  async createTasks(
    tasks: CreateClickUpTaskRequest[]
  ): Promise<CreateClickUpTasksBatchResult[]> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured");
    }

    if (!tasks.length) {
      throw new Error("At least one task is required");
    }

    const response = await fetch(`${getBaseUrl()}/tasks`, {
      method: "POST",
      headers: await getAuthHeaders(),
      body: JSON.stringify({ tasks }),
    });

    if (!response.ok) {
      await parseError(response);
    }

    const data = await response.json();
    return data.results ?? [];
  }

  async listTasksInList(
    teamId: string,
    listId: string,
    includeComments = true
  ): Promise<ClickUpTask[]> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured");
    }

    const params = includeComments ? "?includeComments=true" : "";
    const response = await fetch(
      `${getBaseUrl()}/teams/${encodeURIComponent(teamId)}/lists/${encodeURIComponent(listId)}/tasks${params}`,
      {
        method: "GET",
        headers: await getAuthHeaders(),
      }
    );

    if (!response.ok) {
      await parseError(response);
    }

    const data = await response.json();
    return data.tasks ?? [];
  }

  async listDocs(teamId: string): Promise<ClickUpDoc[]> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured");
    }

    const response = await fetch(
      `${getBaseUrl()}/teams/${encodeURIComponent(teamId)}/docs`,
      {
        method: "GET",
        headers: await getAuthHeaders(),
      }
    );

    if (!response.ok) {
      await parseError(response);
    }

    const data = await response.json();
    return data.docs ?? [];
  }

  async getDocPages(teamId: string, docId: string): Promise<ClickUpDocPage[]> {
    if (!isSupabaseConfigured) {
      throw new Error("Service not configured");
    }

    const response = await fetch(
      `${getBaseUrl()}/teams/${encodeURIComponent(teamId)}/docs/${encodeURIComponent(docId)}/pages`,
      {
        method: "GET",
        headers: await getAuthHeaders(),
      }
    );

    if (!response.ok) {
      await parseError(response);
    }

    const data = await response.json();
    return data.pages ?? [];
  }
}

export const clickupService = new ClickUpService();

export function clickUpTeamsQueryKey(userId: string | undefined): readonly [string, string | undefined] {
  return ["clickup-teams", userId] as const;
}

export function clickUpMembersQueryKey(
  userId: string | undefined,
  teamId: string | undefined
): readonly [string, string | undefined, string | undefined] {
  return ["clickup-members", userId, teamId] as const;
}

export function clickUpListsQueryKey(
  userId: string | undefined,
  teamId: string | undefined
): readonly [string, string | undefined, string | undefined] {
  return ["clickup-lists", userId, teamId] as const;
}

export function clickUpDocsQueryKey(
  userId: string | undefined,
  teamId: string | undefined
): readonly [string, string | undefined, string | undefined] {
  return ["clickup-docs", userId, teamId] as const;
}

export function clickUpListTasksQueryKey(
  userId: string | undefined,
  teamId: string | undefined,
  listId: string | undefined
): readonly [string, string | undefined, string | undefined, string | undefined] {
  return ["clickup-list-tasks", userId, teamId, listId] as const;
}
